// Public, no-login page an event organiser reaches from the emailed signing link
// (/sign/<token>). It guides them through each document in turn — read it, sign
// it — and then says the signed copies have been emailed.
//
// Rendered directly by index.js, OUTSIDE the auth gate, MSAL and the app
// providers, like the kit list and Phone Finder pages, and it fetches with plain
// `fetch` (the app's axios interceptors would attach a token nobody here has, and
// toast into a page with no Notifier). The token is the only credential; the
// backend (app/contracts/public_router.py) validates it and narrows everything to
// this one contract. An expired link isn't an error here: opening one emails a
// fresh link to the address on record, and the page says so.
import { useEffect, useState } from "react";
import {
  Alert, AlertTitle, Box, Button, CircularProgress, Divider, Paper, Stack, Step, StepLabel,
  Stepper, Typography,
} from "@mui/material";
import PdfPreview from "../components/PdfPreview";
import SignaturePad from "../components/SignaturePad";
import { PUBLIC_SIGNING_API, fmtDateTime } from "../contracts";

const LOGO_URL = process.env.PUBLIC_URL + "/ShelterStoneLogo.png";
const CONTACT = "hello@shelter-stone.com";

const tokenFromUrl = () =>
  decodeURIComponent(window.location.pathname.replace(/\/+$/, "").split("/").pop() || "");

async function call(path, options = {}) {
  const res = await fetch(`${PUBLIC_SIGNING_API}/${path}`, {
    ...options,
    headers: { ...(options.body ? { "Content-Type": "application/json" } : {}), ...(options.headers || {}) },
  });
  if (!res.ok) {
    let detail = null;
    try { detail = (await res.json()).detail; } catch { /* not JSON */ }
    const err = new Error(typeof detail === "string" ? detail : `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export default function SignContract() {
  const token = tokenFromUrl();
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("loading");   // loading | ready | notfound | error
  const [error, setError] = useState(null);
  const [signing, setSigning] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [copiesResult, setCopiesResult] = useState(null);

  const load = () => {
    if (!token || token === "sign") { setPhase("notfound"); return; }
    call(encodeURIComponent(token))
      .then(d => { setData(d); setPhase("ready"); })
      .catch(err => setPhase(err.status === 404 ? "notfound" : "error"));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [token]);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    document.title = "Sign documents — Shelter Stone";
    return () => meta.remove();
  }, []);

  const docs = data?.documents || [];
  const currentIndex = docs.findIndex(d => !d.signed);
  const current = currentIndex >= 0 ? docs[currentIndex] : null;

  const sign = (signature) => {
    setSigning(true);
    setError(null);
    call(`${encodeURIComponent(token)}/documents/${current.id}/sign`, {
      method: "POST", body: JSON.stringify({ ...signature, sha256: current.sha256 }),
    })
      .then(d => { setData(d); window.scrollTo({ top: 0, behavior: "smooth" }); })
      .catch(err => {
        setError(err.message);
        if (err.status === 410 || err.status === 409) load();
      })
      .finally(() => setSigning(false));
  };

  const emailCopies = () => {
    setEmailing(true);
    call(`${encodeURIComponent(token)}/email-copies`, { method: "POST" })
      .then(d => { setData(d); setCopiesResult(d.emailed ? "sent" : (d.detail || "recent")); })
      .catch(err => setCopiesResult(err.message))
      .finally(() => setEmailing(false));
  };

  const hint = data?.email_hint || "your email address";
  // Say "document" or "documents" as it really is — most organisers sign just an
  // event schedule. One document goes by its title ("The Event Schedule").
  const many = (data?.document_count ?? 1) > 1;
  const single = data?.document_titles?.[0] || "document";
  const w = many
    ? { the: "These documents", the_lc: "the documents", copies: "signed copies", it: "them", have: "have", it_s: "they've" }
    : { the: `The ${single}`, the_lc: `the ${single}`, copies: "signed copy", it: "it", have: "has", it_s: "it's" };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: 2 }}>
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <img src={LOGO_URL} alt="Shelter Stone" style={{ maxWidth: 260, width: "100%" }} />
      </Box>

      {phase === "loading" && (
        <Stack sx={{ alignItems: "center", py: 6 }} spacing={2}>
          <CircularProgress />
          <Typography color="text.secondary">Loading your documents…</Typography>
        </Stack>
      )}

      {phase === "notfound" && (
        <Alert severity="warning">
          <AlertTitle>Link not recognised</AlertTitle>
          This signing link doesn't match any documents. Check you've used the whole link from
          the email, or contact Shelter Stone at {CONTACT}.
        </Alert>
      )}

      {phase === "error" && (
        <Alert severity="error" action={<Button color="inherit" onClick={load}>Retry</Button>}>
          <AlertTitle>Couldn't load the documents</AlertTitle>
          Please check your connection and try again.
        </Alert>
      )}

      {phase === "ready" && data && (
        <Stack spacing={2}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {data.event_label ? `${data.event_label} — ` : ""}{many ? "documents" : single.toLowerCase()} to sign
            </Typography>
            {data.signer_name && (
              <Typography color="text.secondary">For {data.signer_name}</Typography>
            )}
          </Box>

          {data.state === "renewed" && (
            <Alert severity="info">
              <AlertTitle>This link has expired</AlertTitle>
              We've emailed a fresh signing link to {hint}. Please open {w.the_lc} from that
              email instead — this link no longer works.
            </Alert>
          )}
          {data.state === "renew_suppressed" && (
            <Alert severity="info">
              <AlertTitle>This link is no longer valid</AlertTitle>
              A fresh signing link was emailed to {hint} a few minutes ago. Please use the link in
              that email (it may be in your junk folder).
            </Alert>
          )}
          {data.state === "voided" && (
            <Alert severity="warning">
              <AlertTitle>{w.the} {w.have} been withdrawn</AlertTitle>
              Shelter Stone has withdrawn {w.the_lc}, so {many ? "they" : "it"} can no longer be
              signed. Please contact {CONTACT} if you weren't expecting this.
            </Alert>
          )}
          {data.state === "processing" && (
            <Alert severity="success">
              <AlertTitle>Signed — thank you</AlertTitle>
              {many ? "Everything has been signed. The signed copies are" : "It has been signed. The signed copy is"}
              {" "}being prepared and will be emailed to {hint}.
            </Alert>
          )}
          {data.state === "completed" && (
            <Alert severity="success">
              <AlertTitle>{w.the} {w.have} been signed</AlertTitle>
              {many ? "They have" : "It has"} been signed by both parties, and the {w.copies}
              {" "}{many ? "have" : "has"} been emailed to {hint}.
              {copiesResult === null && data.can_email_copies && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" gutterBottom>Would you like {w.it} emailed again?</Typography>
                  <Button variant="outlined" size="small" disabled={emailing} onClick={emailCopies}
                          startIcon={emailing ? <CircularProgress size={14} /> : undefined}>
                    Email {w.it} to me again
                  </Button>
                </Box>
              )}
              {copiesResult === "sent" && (
                <Typography variant="body2" sx={{ mt: 1 }}>Done — {w.it_s} been emailed to {hint} again.</Typography>
              )}
              {copiesResult && copiesResult !== "sent" && (
                <Typography variant="body2" sx={{ mt: 1 }}>{copiesResult}</Typography>
              )}
            </Alert>
          )}

          {data.state === "active" && current && (
            <>
              {docs.length > 1 && (
                <Stepper activeStep={currentIndex} alternativeLabel>
                  {docs.map(d => <Step key={d.id} completed={d.signed}><StepLabel>{d.title}</StepLabel></Step>)}
                </Stepper>
              )}
              <Typography variant="body2" color="text.secondary">
                {docs.length > 1
                  ? `There are ${docs.length} documents to sign, one after the other. This is ${currentIndex + 1} of ${docs.length}.`
                  : "Please read the document, then sign at the bottom of the page."}
                {data.expires_at ? ` This link works until ${fmtDateTime(data.expires_at)}.` : ""}
              </Typography>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Typography variant="h6">{current.title}</Typography>
                  {current.shelter_stone && (
                    <Typography variant="body2" color="text.secondary">
                      Signed for Shelter Stone by {current.shelter_stone.name} on{" "}
                      {fmtDateTime(current.shelter_stone.signed_at)}.
                    </Typography>
                  )}
                  <PdfPreview title={current.title} height={620}
                              reloadKey={`${current.id}-${current.sha256}`}
                              load={() => {
                                // The token link itself, ending in the file's name, so the
                                // viewer's own Save names it as Download does.
                                const url = `${PUBLIC_SIGNING_API}/${encodeURIComponent(token)}/documents/${current.id}/pdf/${encodeURIComponent(current.filename || "document.pdf")}`;
                                return Promise.resolve({ url, downloadUrl: `${url}?download=true` });
                              }} />
                  <Divider />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Sign {current.title}</Typography>
                  {error && <Alert severity="error">{error}</Alert>}
                  <SignaturePad key={current.id} defaultName={data.signer_name || ""} busy={signing}
                                consentText={data.consent_text}
                                submitLabel={currentIndex < docs.length - 1 ? "I Agree and Sign — next document" : undefined}
                                onSign={sign} />
                </Stack>
              </Paper>
            </>
          )}

          <Typography variant="caption" color="text.secondary">
            Questions? Contact Shelter Stone Medical and Rescue Services Ltd at {CONTACT}.
          </Typography>
        </Stack>
      )}
    </Box>
  );
}
